import { computed, effect, inject } from '@angular/core';
import {
  getState,
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import dayjs from 'dayjs';
import { GetPriceTermRequest } from './../../../shared/models/api/flight/get-price-term.model';
import { findBookingConditionForFareClass } from './booking-condition.matcher';
import { PrefetchBookingNotifyModel } from './../../../shared/models/api/notify/prefetch-booking-notify.model';
import {
  PriceTermState,
  ReselectFlightState,
  SelectFlightConditionsState,
} from './../../../shared/models/app/booking-state.model';

import { environment } from '../../../../environments/environment';
import { GenderType, STORAGE_KEYS } from '../../../shared/constants';
import {
  BookingStatus,
  FlightType,
  PassengerType,
  PaymentMethod,
  SortPriceType,
} from '../../../shared/enums';
import {
  BookingState,
  PassengerInfoState,
  PaymentState,
  PaymentSuccessState,
  SelectFlightFilter,
  SelectFlightState,
  SelectServiceState,
} from '../../../shared/models/app';

import {
  Baggage,
  FareData,
  Flight,
  GetMyBookingDetailResponse,
  Passenger,
  Segment,
} from '../../../shared/models/api/booking';
import {
  BookingConditionSearch,
  FlightsData,
  PriceFlights,
  SegmentFlights,
} from '../../../shared/models/api/flight';
import {
  MyBBookingAncillary,
  MyBBookingPassenger,
  MybCheckBookingResponse,
  MyBCreateBookingRequest,
  MyBCreateBookingResponse,
  MybMyServicesResponse,
} from '../../../shared/models/api/portal-booking';
import { LocalStorageService } from '../../../shared/services/app';

const initialState = Object.freeze<BookingState>({
  sessionTimeOutRef: null,
  sessionExpired: null,
  filter: {
    taxIncluded: true,
    sortPrice: SortPriceType.Ascending,
    transits: [],
    returnTransits: [],
    airlines: [],
    systems: [],
    departureTime: [],
    arrivalTime: [],
    priceFrom: null,
    priceTo: null,
  },
  servicesByVendor: [],
  priceTerms: [],
  selectFlightConditions: [],
  selectFlight: {
    code: null,
    from: null,
    to: null,
    date: null,
    returnDate: null,
    roundTrip: false,
    local: false,
    adult: 1,
    child: 0,
    infant: 0,
    flights: [],
    roundTripFlights: [],
    smartCombos: [],
    selectedFlight: null,
    selectedRoundTripFlight: null,
    selectedSmartCombo: null,
    isLoadingFlights: false,
    isLoadingCount: 0,
    showCompact: false,
    showCheapPrices: false,
    showSmartCombo: true,
    showSmartComboButton: false,
  },
  reselectFlight: {
    selectedFlightGroup: null,
    selectedRoundTripFlightGroup: null,
    selectedFlights: null,
  },
  passengerInfo: {
    passengers: [],
    contactPerson: {
      gender: GenderType.Male,
      fullName: '',
      phone: '',
      email: '',
    },
  },
  selectService: {
    ancillaries: null,
  },
  bookingRequest: null,
  isLookingForBooking: false,
  bookingChecked: null,
  bookingCreated: null,
  payment: {
    paymentOption: null,
    paymentFee: null,
    paymentUrl: null,
    isBookReservation: false,
    isBookIssue: false,
  },
  paymentSuccess: {
    paramBookingId: null,
    preloadTranId: null,
    preloadReviewBooking: null,
    reviewBooking: null,
    paymentMethod: null,
    contactInfo: null,
  },
});

export const BookingStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ selectFlight, filter }) => ({
    selectingFlightType: computed(() => {
      if (selectFlight.roundTrip() && selectFlight.selectedFlight()) {
        return FlightType.two;
      }
      return FlightType.one;
    }),
    flightsFiltered: computed(() => {
      // Sort Price
      const flights = [
        ...selectFlight.flights(),
        ...selectFlight.smartCombos(),
      ];

      let filtered = flights.sort((a, b) => {
        const aPrice =
          a.type === 'combo' ? a.prices[0] : a.flights[0].prices[0];
        const bPrice =
          b.type === 'combo' ? b.prices[0] : b.flights[0].prices[0];
        return (
          aPrice.price +
          aPrice.tax +
          aPrice.fee -
          (bPrice.price + bPrice.tax + bPrice.fee)
        );
      });
      if (filter.sortPrice() === SortPriceType.Descending) {
        filtered = filtered.reverse();
      }

      // Transits
      if (filter.transits() && filter.transits().length > 0) {
        filtered = filtered.filter((flight) => {
          const flightSegmentsCount = flight.flights[0].segments.length;
          return filter
            .transits()
            .includes(flightSegmentsCount > 3 ? 3 : flightSegmentsCount);
        });
      }

      // Return Transits
      if (filter.returnTransits() && filter.returnTransits().length > 0) {
        filtered = filtered.filter((flight) => {
          if (flight.type === 'combo' && flight.flights[1]) {
            const segments = flight.flights[1].segments;
            return filter
              .returnTransits()
              .includes(segments.length > 3 ? 3 : segments.length);
          }
          return true;
        });
      }

      // Airlines
      if (
        filter.airlines() &&
        filter.airlines().length > 0 &&
        !filter.airlines().includes('-1')
      ) {
        filtered = filtered.filter((flight) => {
          if (flight.type === 'combo') {
            return flight.flights.some((f) => {
              return filter.airlines().includes(f.segments[0].airline);
            });
          }
          return filter
            .airlines()
            .includes(flight.flights[0].segments[0].airline);
        });
      }

      // Systems
      if (filter.systems() && filter.systems().length > 0) {
        const systems = filter.systems();
        const includeOthers = systems.includes('-1');
        filtered = filtered.filter((flight) => {
          if (flight.type === 'combo') {
            if (systems.includes(flight.source)) {
              return true;
            }
            if (includeOthers && !flight.source) {
              return true;
            }
            return false;
          }

          if (flight.flights.some((f) => systems.includes(f.source))) {
            return true;
          }
          if (includeOthers && !flight.flights[0].source) {
            return true;
          }
          return false;
        });
      }

      // Departure Time
      if (filter.departureTime() && filter.departureTime().length > 0) {
        filtered = filtered.filter((flight) => {
          const flightsToCheck =
            flight.type === 'combo' ? flight.flights : [flight.flights[0]];
          return flightsToCheck.some((f) => {
            const departureTime = dayjs(f.segments[0].startDate);
            return filter.departureTime().some((times) => {
              let [start, end] = times.split(' - ');
              end = dayjs(end, 'HH:mm').subtract(1, 'minute').format('HH:mm');
              const rangeStart = dayjs(departureTime)
                .set('hour', dayjs(start, 'HH:mm').hour())
                .set('minute', dayjs(start, 'HH:mm').minute());
              const rangeEnd = dayjs(departureTime)
                .set('hour', dayjs(end, 'HH:mm').hour())
                .set('minute', dayjs(end, 'HH:mm').minute());
              return departureTime.isBetween(
                rangeStart,
                rangeEnd,
                'minute',
                '[]',
              );
            });
          });
        });
      }

      // Arrival Time
      if (filter.arrivalTime() && filter.arrivalTime().length > 0) {
        filtered = filtered.filter((flight) => {
          const flightsToCheck =
            flight.type === 'combo' ? flight.flights : [flight.flights[0]];
          return flightsToCheck.some((f) => {
            const arrivalTime = dayjs(
              f.segments[f.segments.length - 1].endDate,
            );
            return filter.arrivalTime().some((times) => {
              let [start, end] = times.split(' - ');
              end = dayjs(end, 'HH:mm').subtract(1, 'minute').format('HH:mm');
              const rangeStart = dayjs(arrivalTime)
                .set('hour', dayjs(start, 'HH:mm').hour())
                .set('minute', dayjs(start, 'HH:mm').minute());
              const rangeEnd = dayjs(arrivalTime)
                .set('hour', dayjs(end, 'HH:mm').hour())
                .set('minute', dayjs(end, 'HH:mm').minute());
              return arrivalTime.isBetween(
                rangeStart,
                rangeEnd,
                'minute',
                '[]',
              );
            });
          });
        });
      }

      // Price
      if (filter.priceFrom()) {
        filtered = filtered.filter((flight) => {
          const prices =
            flight.type === 'combo' ? flight.prices : flight.flights[0].prices;
          const adultPrice = prices.find((p) => p.type === PassengerType.Adult);
          if (adultPrice) {
            const comparePrice = filter.taxIncluded()
              ? adultPrice.price + adultPrice.tax + adultPrice.fee
              : adultPrice.price;
            return comparePrice >= filter.priceFrom()!;
          }
          return true;
        });
      }
      if (filter.priceTo()) {
        filtered = filtered.filter((flight) => {
          const prices =
            flight.type === 'combo' ? flight.prices : flight.flights[0].prices;
          const adultPrice = prices.find((p) => p.type === PassengerType.Adult);
          if (adultPrice) {
            const comparePrice = filter.taxIncluded()
              ? adultPrice.price + adultPrice.tax + adultPrice.fee
              : adultPrice.price;
            return comparePrice <= filter.priceTo()!;
          }
          return true;
        });
      }

      // Remove A1 Airline
      filtered = filtered.filter((flight) => {
        if (flight.type === 'combo') {
          return (
            flight.flights[0].segments[0].airline !== 'A1' &&
            flight.flights[1]?.segments[0]?.airline !== 'A1'
          );
        }
        return flight.flights[0].segments[0].airline !== 'A1';
      });

      return filtered;
    }),
    roundTripFlightsFiltered: computed(() => {
      // Sort Price
      const flights = [
        ...selectFlight.roundTripFlights(),
        ...selectFlight.smartCombos(),
      ];

      let filtered = flights.sort((a, b) => {
        const aPrice =
          a.type === 'combo' ? a.prices[0] : a.flights[0].prices[0];
        const bPrice =
          b.type === 'combo' ? b.prices[0] : b.flights[0].prices[0];
        return (
          aPrice.price +
          aPrice.tax +
          aPrice.fee -
          (bPrice.price + bPrice.tax + bPrice.fee)
        );
      });
      if (filter.sortPrice() === SortPriceType.Descending) {
        filtered = filtered.reverse();
      }

      // Transits
      if (filter.transits() && filter.transits().length > 0) {
        filtered = filtered.filter((flight) => {
          const flightSegmentsCount = flight.flights[0].segments.length;
          return filter
            .transits()
            .includes(flightSegmentsCount > 3 ? 3 : flightSegmentsCount);
        });
      }

      // Return Transits
      if (filter.returnTransits() && filter.returnTransits().length > 0) {
        filtered = filtered.filter((flight) => {
          if (flight.type === 'combo' && flight.flights[1]) {
            const segments = flight.flights[1].segments;
            return filter
              .returnTransits()
              .includes(segments.length > 3 ? 3 : segments.length);
          }
          if (flight.type !== 'combo' && flight.flights[1]) {
            const flightSegmentsCount = flight.flights[1].segments.length;
            return filter
              .returnTransits()
              .includes(flightSegmentsCount > 3 ? 3 : flightSegmentsCount);
          }
          return true;
        });
      }

      // Airlines
      if (
        filter.airlines() &&
        filter.airlines().length > 0 &&
        !filter.airlines().includes('-1')
      ) {
        filtered = filtered.filter((flight) => {
          if (flight.type === 'combo') {
            return flight.flights.some((f) => {
              return filter.airlines().includes(f.segments[0].airline);
            });
          }
          return filter
            .airlines()
            .includes(flight.flights[0].segments[0].airline);
        });
      }

      // Systems
      if (filter.systems() && filter.systems().length > 0) {
        const systems = filter.systems();
        const includeOthers = systems.includes('-1');
        filtered = filtered.filter((flight) => {
          if (flight.type === 'combo') {
            if (systems.includes(flight.source)) {
              return true;
            }
            if (includeOthers && !flight.source) {
              return true;
            }
            return false;
          }

          if (flight.flights.some((f) => systems.includes(f.source))) {
            return true;
          }
          if (includeOthers && !flight.flights[0].source) {
            return true;
          }
          return false;
        });
      }

      // Departure Time
      if (filter.departureTime() && filter.departureTime().length > 0) {
        filtered = filtered.filter((flight) => {
          const flightsToCheck =
            flight.type === 'combo' ? flight.flights : [flight.flights[0]];
          return flightsToCheck.some((f) => {
            const departureTime = dayjs(f.segments[0].startDate);
            return filter.departureTime().some((times) => {
              let [start, end] = times.split(' - ');
              end = dayjs(end, 'HH:mm').subtract(1, 'minute').format('HH:mm');
              const rangeStart = dayjs(departureTime)
                .set('hour', dayjs(start, 'HH:mm').hour())
                .set('minute', dayjs(start, 'HH:mm').minute());
              const rangeEnd = dayjs(departureTime)
                .set('hour', dayjs(end, 'HH:mm').hour())
                .set('minute', dayjs(end, 'HH:mm').minute());
              return departureTime.isBetween(
                rangeStart,
                rangeEnd,
                'minute',
                '[]',
              );
            });
          });
        });
      }

      // Arrival Time
      if (filter.arrivalTime() && filter.arrivalTime().length > 0) {
        filtered = filtered.filter((flight) => {
          const flightsToCheck =
            flight.type === 'combo' ? flight.flights : [flight.flights[0]];
          return flightsToCheck.some((f) => {
            const arrivalTime = dayjs(
              f.segments[f.segments.length - 1].endDate,
            );
            return filter.arrivalTime().some((times) => {
              let [start, end] = times.split(' - ');
              end = dayjs(end, 'HH:mm').subtract(1, 'minute').format('HH:mm');
              const rangeStart = dayjs(arrivalTime)
                .set('hour', dayjs(start, 'HH:mm').hour())
                .set('minute', dayjs(start, 'HH:mm').minute());
              const rangeEnd = dayjs(arrivalTime)
                .set('hour', dayjs(end, 'HH:mm').hour())
                .set('minute', dayjs(end, 'HH:mm').minute());
              return arrivalTime.isBetween(
                rangeStart,
                rangeEnd,
                'minute',
                '[]',
              );
            });
          });
        });
      }

      // Price
      if (filter.priceFrom()) {
        filtered = filtered.filter((flight) => {
          const prices =
            flight.type === 'combo' ? flight.prices : flight.flights[0].prices;
          const adultPrice = prices.find(
            (p: any) => p.type === PassengerType.Adult,
          );
          if (adultPrice) {
            const comparePrice = filter.taxIncluded()
              ? adultPrice.price + adultPrice.tax + adultPrice.fee
              : adultPrice.price;
            return comparePrice >= filter.priceFrom()!;
          }
          return true;
        });
      }
      if (filter.priceTo()) {
        filtered = filtered.filter((flight) => {
          const prices =
            flight.type === 'combo' ? flight.prices : flight.flights[0].prices;
          const adultPrice = prices.find(
            (p: any) => p.type === PassengerType.Adult,
          );
          if (adultPrice) {
            const comparePrice = filter.taxIncluded()
              ? adultPrice.price + adultPrice.tax + adultPrice.fee
              : adultPrice.price;
            return comparePrice <= filter.priceTo()!;
          }
          return true;
        });
      }

      // Remove A1 Airline
      filtered = filtered.filter((flight) => {
        if (flight.type === 'combo') {
          return (
            flight.flights[0].segments[0].airline !== 'A1' &&
            flight.flights[1]?.segments[0]?.airline !== 'A1'
          );
        }
        return flight.flights[0].segments[0].airline !== 'A1';
      });

      return filtered;
    }),
    smartCombosFiltered: computed(() => {
      // Sort Price
      let filtered = selectFlight.smartCombos().sort((a, b) => {
        const aPrice = a.prices[0];
        const bPrice = b.prices[0];
        return (
          aPrice.price +
          aPrice.tax +
          aPrice.fee -
          (bPrice.price + bPrice.tax + bPrice.fee)
        );
      });
      if (filter.sortPrice() === SortPriceType.Descending) {
        filtered = filtered.reverse();
      }

      // Transits
      if (filter.transits() && filter.transits().length > 0) {
        filtered = filtered.filter((combo) => {
          const segments = combo.flights[0].segments;
          return filter
            .transits()
            .includes(segments.length > 3 ? 3 : segments.length);
        });
      }

      // Return Transits
      if (filter.returnTransits() && filter.returnTransits().length > 0) {
        filtered = filtered.filter((combo) => {
          const segments = combo.flights[1].segments;
          return filter
            .returnTransits()
            .includes(segments.length > 3 ? 3 : segments.length);
        });
      }

      // Airlines
      if (
        filter.airlines() &&
        filter.airlines().length > 0 &&
        !filter.airlines().includes('-1')
      ) {
        filtered = filtered.filter((combo) => {
          return combo.flights.some((flight) => {
            return filter.airlines().includes(flight.segments[0].airline);
          });
        });
      }

      // Systems
      if (filter.systems() && filter.systems().length > 0) {
        const systems = filter.systems();
        const includeOthers = systems.includes('-1');
        filtered = filtered.filter((flight) => {
          if (systems.includes(flight.source)) {
            return true;
          }
          if (includeOthers && !flight.source) {
            return true;
          }
          return false;
        });
      }

      // Departure Time
      if (filter.departureTime() && filter.departureTime().length > 0) {
        filtered = filtered.filter((combo) => {
          return combo.flights.some((flight) => {
            const departureTime = dayjs(flight.segments[0].startDate);
            return filter.departureTime().some((times) => {
              let [start, end] = times.split(' - ');
              end = dayjs(end, 'HH:mm').subtract(1, 'minute').format('HH:mm');
              const rangeStart = dayjs(departureTime)
                .set('hour', dayjs(start, 'HH:mm').hour())
                .set('minute', dayjs(start, 'HH:mm').minute());
              const rangeEnd = dayjs(departureTime)
                .set('hour', dayjs(end, 'HH:mm').hour())
                .set('minute', dayjs(end, 'HH:mm').minute());
              return departureTime.isBetween(
                rangeStart,
                rangeEnd,
                'minute',
                '[]',
              );
            });
          });
        });
      }

      // Arrival Time
      if (filter.arrivalTime() && filter.arrivalTime().length > 0) {
        filtered = filtered.filter((combo) => {
          return combo.flights.some((flight) => {
            const arrivalTime = dayjs(
              flight.segments[flight.segments.length - 1].endDate,
            );
            return filter.arrivalTime().some((times) => {
              let [start, end] = times.split(' - ');
              end = dayjs(end, 'HH:mm').subtract(1, 'minute').format('HH:mm');
              const rangeStart = dayjs(arrivalTime)
                .set('hour', dayjs(start, 'HH:mm').hour())
                .set('minute', dayjs(start, 'HH:mm').minute());
              const rangeEnd = dayjs(arrivalTime)
                .set('hour', dayjs(end, 'HH:mm').hour())
                .set('minute', dayjs(end, 'HH:mm').minute());
              return arrivalTime.isBetween(
                rangeStart,
                rangeEnd,
                'minute',
                '[]',
              );
            });
          });
        });
      }

      // Price
      if (filter.priceFrom()) {
        filtered = filtered.filter((flight) => {
          const adultPrice = flight.prices.find(
            (p) => p.type === PassengerType.Adult,
          );
          if (adultPrice) {
            const comparePrice = filter.taxIncluded()
              ? adultPrice.price + adultPrice.tax + adultPrice.fee
              : adultPrice.price;
            return comparePrice >= filter.priceFrom()!;
          }
          return true;
        });
      }
      if (filter.priceTo()) {
        filtered = filtered.filter((flight) => {
          const adultPrice = flight.prices.find(
            (p) => p.type === PassengerType.Adult,
          );
          if (adultPrice) {
            const comparePrice = filter.taxIncluded()
              ? adultPrice.price + adultPrice.tax + adultPrice.fee
              : adultPrice.price;
            return comparePrice <= filter.priceTo()!;
          }
          return true;
        });
      }

      // Remove A1 Airline
      filtered = filtered.filter((combo) => {
        return (
          combo.flights[0].segments[0].airline !== 'A1' &&
          combo.flights[1].segments[0].airline !== 'A1'
        );
      });

      return filtered;
    }),
  })),
  withComputed(({ sessionExpired }) => ({
    /**
     * Time remaining in milliseconds
     */
    remainingSessionTime: computed(() => {
      if (sessionExpired()) {
        const remaining = dayjs(sessionExpired()).diff(dayjs(), 'milliseconds');
        return remaining;
      }
      return null;
    }),
    isSessionValid: computed(() => {
      if (sessionExpired()) {
        const session = dayjs(sessionExpired());
        return session > dayjs();
      }
      return false;
    }),
  })),
  withComputed(({ paymentSuccess }) => ({
    getReviewBooking: computed(() => {
      const reviewBooking = paymentSuccess.reviewBooking();
      if (reviewBooking) {
        return reviewBooking;
      }

      const paramBookingId = paymentSuccess.paramBookingId();
      const preloadTranId = paymentSuccess.preloadTranId();
      if (
        !paramBookingId ||
        (paramBookingId && paramBookingId === preloadTranId)
      ) {
        return paymentSuccess.preloadReviewBooking();
      }

      return reviewBooking;
    }),
  })),
  withMethods((store, localStorageService = inject(LocalStorageService)) => ({
    getFullState() {
      return getState(store);
    },

    generateSessionTime(minutes: number | null) {
      if (minutes) {
        let sessionExpired = dayjs().add(minutes, 'minute').toDate();
        patchState(store, () => ({
          sessionExpired,
        }));
      } else {
        patchState(store, () => ({
          sessionExpired: null,
        }));
      }
    },

    setSessionTimeout(timeoutRef: NodeJS.Timeout | null) {
      const currentTimeoutRef = store.sessionTimeOutRef();
      if (currentTimeoutRef) {
        clearTimeout(currentTimeoutRef);
      }

      patchState(store, () => ({
        sessionTimeOutRef: timeoutRef,
      }));
    },

    saveSession() {
      let state = getState(store);
      state = {
        ...state,
        selectFlight: {
          ...state.selectFlight,
          flights: [],
          roundTripFlights: [],
          smartCombos: [],
        },
      };
      localStorageService.setEncrypt(STORAGE_KEYS.SESSION, state);
    },

    restoreSession() {
      const state = localStorageService.getEncrypt<BookingState>(
        STORAGE_KEYS.SESSION,
      );
      if (state) {
        patchState(store, (currentState) => ({
          ...currentState,
          ...state,
        }));
      }
    },
  })),
  withMethods((store, localStorageService = inject(LocalStorageService)) => ({
    updateFilter(filter: Partial<SelectFlightFilter>) {
      patchState(store, (state) => ({
        filter: {
          ...state.filter,
          ...filter,
        },
      }));
    },

    resetFilter() {
      patchState(store, () => ({
        filter: { ...initialState.filter },
      }));
    },

    updateServicesByVendor(servicesByVendor: Array<MybMyServicesResponse>) {
      patchState(store, () => ({ servicesByVendor }));
    },

    getPriceTerm(request: GetPriceTermRequest) {
      let priceTerms = store.priceTerms();
      if (!priceTerms || priceTerms.length === 0) {
        priceTerms =
          localStorageService.get<PriceTermState[]>(STORAGE_KEYS.PRICE_TERMS) ||
          [];
        patchState(store, () => ({ priceTerms: [...priceTerms] }));
      }
      const priceTerm = priceTerms.find(
        (pt) =>
          pt.request.airline === request.airline &&
          pt.request.fareClass === request.fareClass &&
          pt.request.groupClass === request.groupClass,
      );
      if (priceTerm) {
        if (
          priceTerm.response &&
          priceTerm.expiredDate &&
          dayjs(priceTerm.expiredDate).isAfter(dayjs())
        ) {
          return priceTerm;
        } else if (priceTerm.loadStart) {
          if (dayjs(priceTerm.loadStart).add(5, 'minutes').isAfter(dayjs())) {
            return priceTerm; // Still loading
          } else {
            return null; // Expired loading
          }
        } else {
          return null; // Not loaded yet / Expired
        }
      }
      return null;
    },

    resetPriceTerms() {
      patchState(store, () => ({ priceTerms: [] }));
    },

    addOrUpdatePriceTerm(data: PriceTermState) {
      const storagePriceTerms =
        localStorageService.get<PriceTermState[]>(STORAGE_KEYS.PRICE_TERMS) ||
        [];
      const index = storagePriceTerms.findIndex(
        (pt) =>
          pt.request.airline === data.request.airline &&
          pt.request.fareClass === data.request.fareClass &&
          pt.request.groupClass === data.request.groupClass,
      );
      if (index >= 0) {
        storagePriceTerms[index] = {
          request: data.request,
          response: data.response,
          loadStart: data.loadStart,
          expiredDate: data.expiredDate,
        };
      } else {
        storagePriceTerms.push({
          request: data.request,
          response: data.response,
          loadStart: data.loadStart,
          expiredDate: data.expiredDate,
        });
      }

      localStorageService.set(STORAGE_KEYS.PRICE_TERMS, storagePriceTerms);
      patchState(store, () => ({ priceTerms: [...storagePriceTerms] }));
    },

    getSelectFlightConditions(airlineRequest: string, fareClass: string) {
      const conditions = store
        .selectFlightConditions()
        .find((c) => c.airlineRequest === airlineRequest);
      if (!conditions) {
        return [];
      }

      // Khớp theo đuôi dài nhất (Z1DLX → DLX) thay vì tách "_": VJ đã bỏ dấu "_"
      // trong mã hạng nên cách cũ trượt và mọi hạng VJ rơi về DEFAULT.
      return (
        findBookingConditionForFareClass(conditions.conditions, fareClass)
          ?.bookingRules ?? []
      );
    },

    updateSelectFlightConditionsState(
      airlineRequest: string,
      conditions: Array<BookingConditionSearch>,
    ) {
      patchState(store, (state) => {
        const existingCondition = state.selectFlightConditions.find(
          (c) => c.airlineRequest === airlineRequest,
        );
        let updatedConditions: SelectFlightConditionsState[] = [];
        if (existingCondition) {
          updatedConditions = state.selectFlightConditions.map((c) => {
            if (c.airlineRequest === airlineRequest) {
              return { airlineRequest, conditions };
            }
            return c;
          });
        } else {
          updatedConditions = [
            ...state.selectFlightConditions,
            { airlineRequest, conditions },
          ];
        }
        return {
          selectFlightConditions: [...updatedConditions],
        };
      });
    },

    updateSelectFlightState(selectFlightState: Partial<SelectFlightState>) {
      patchState(store, (state) => ({
        selectFlight: {
          ...state.selectFlight,
          ...selectFlightState,
        },
      }));
    },

    updateReselectFlightState(
      reselectFlightState: Partial<ReselectFlightState>,
    ) {
      patchState(store, (state) => ({
        reselectFlight: {
          ...state.reselectFlight,
          ...reselectFlightState,
        },
      }));
    },

    resetReselectFlightState() {
      patchState(store, () => ({
        reselectFlight: { ...initialState.reselectFlight },
      }));
    },

    resetSelectFlightConditionsState() {
      patchState(store, () => ({
        selectFlightConditions: [],
      }));
    },

    resetSelectFlightState() {
      this.resetPriceTerms();
      this.resetPassengerInfoState();
      this.resetSelectServiceState();
      this.resetPaymentState();
      this.resetPaymentSuccessState();
      this.resetReselectFlightState();
      patchState(store, () => {
        const sessionTimeOutRef = store.sessionTimeOutRef();
        if (sessionTimeOutRef) {
          clearTimeout(sessionTimeOutRef);
        }
        return {
          sessionTimeOutRef: null,
          sessionExpired: null,
          bookingRequest: null,
          bookingChecked: null,
          bookingCreated: null,
          selectFlight: { ...initialState.selectFlight },
        };
      });
    },

    updatePassengerInfoState(passengerInfoState: Partial<PassengerInfoState>) {
      patchState(store, (state) => ({
        passengerInfo: {
          ...state.passengerInfo,
          ...passengerInfoState,
        },
      }));
    },

    resetPassengerInfoState() {
      patchState(store, () => ({
        passengerInfo: { ...initialState.passengerInfo },
      }));
    },

    updateSelectServiceState(selectServiceState: Partial<SelectServiceState>) {
      patchState(store, (state) => ({
        selectService: {
          ...state.selectService,
          ...selectServiceState,
        },
      }));
    },

    resetSelectServiceState() {
      patchState(store, () => ({
        selectService: { ...initialState.selectService },
      }));
    },

    updateBookingRequest(bookingRequest: MyBCreateBookingRequest) {
      patchState(store, (state) => ({
        bookingRequest: {
          ...state.bookingRequest,
          ...bookingRequest,
        },
      }));
    },

    updateLookingForBooking(isLookingForBooking: boolean) {
      patchState(store, () => ({ isLookingForBooking }));
    },

    generatePreloadReviewBooking(newTotalFare = 0) {
      const mapBookingRequestToReviewBooking = function (
        state: BookingState,
      ): GetMyBookingDetailResponse {
        const data = state.bookingRequest!;

        // Create fake booking date
        const createDate = new Date();
        const expiredDate = new Date(
          new Date(createDate).setTime(
            createDate.getTime() + 2 * 60 * 60 * 1000,
          ),
        ); // 2 hours later

        // Create the flight data for FareData
        const fareData: FareData[] = data.flights.map((flight: FlightsData) => {
          // Create the segments
          const segments: Segment[] = flight.segments.map(
            (segment: SegmentFlights, index: number, arr: SegmentFlights[]) => {
              const mappedSegment: Segment = {
                id: index + 1,
                airline: segment.airline,
                airlineName: segment.airlineName,
                startPoint: segment.startPoint,
                endPoint: segment.endPoint,
                startPointName: segment.startPointName,
                endPointName: segment.endPointName,
                startTime: segment.startDate.toString(),
                endTime: segment.endDate.toString(),
                startTm: new Date(segment.startDate).toLocaleTimeString(),
                endTm: new Date(segment.endDate).toLocaleTimeString(),
                flightNumber: segment.flightNo,
                flightNoMain: segment.flightNo,
                duration: segment.flightTime,
                class: segment.groupClass,
                plane: '',
                startTerminal: '',
                endTerminal: '',
                hasStop: false,
                stopPoint: '',
                stopTime: 0,
                dayChange: false,
                stopOvernight: false,
                changeStation: false,
                changeAirport: false,
                lastItem: index === arr.length - 1,
                handBaggage: '',
                allowanceBaggage: '',
              };

              return mappedSegment;
            },
          );

          // Calculate total net price
          const totalNetPrice = flight.prices.reduce(
            (sum: number, price: PriceFlights) => {
              const priceAndFee = price.price + price.tax + price.fee;
              switch (price.type) {
                case PassengerType.Adult:
                  return sum + priceAndFee * data.adult;
                case PassengerType.Child:
                  return sum + priceAndFee * data.child;
                case PassengerType.Infant:
                  return sum + priceAndFee * data.infant;
              }
              return sum + priceAndFee;
            },
            0,
          );
          // Calculate total price including ancillaries
          const totalPrice =
            totalNetPrice +
            data.listPassenger
              .flatMap((p) => p.ancillaries)
              .filter((anc) => anc.leg === flight.leg)
              .reduce((total, anc) => {
                return total + anc.price;
              }, 0);

          // Build flight object
          const listFlight: Flight[] = [
            {
              seesionId: 0,
              bookingKey: flight.token,
              flightId: flight.flightId,
              leg: flight.leg,
              airline: segments[0]?.airline || '',
              airlineName: segments[0]?.airlineName || '',
              startPoint: segments[0]?.startPoint || '',
              startPointName: segments[0]?.startPointName || '',
              endPoint: segments[segments.length - 1]?.endPoint || '',
              endPointName: segments[segments.length - 1]?.endPointName || '',
              startDate: segments[0]?.startTime || '',
              endDate: segments[segments.length - 1]?.endTime || '',
              flightNumber: flight.flightNumber,
              stopNum: segments.length - 1,
              hasDownStop: segments.length > 1,
              duration: segments.reduce(
                (sum: number, seg: any) => sum + seg.duration,
                0,
              ),
              noRefund: false,
              groupClass: flight.groupClass,
              fareClass: flight.fareClass,
              fareBasis: '',
              fareType: '',
              noLuggage: false,
              promo: false,
              promoCode: '',
              flightValue: flight.flightValue,
              coin: 0,
              operating: segments[0]?.airline || '',
              isVenture: false,
              isRateCurrency: false,
              ventureFrom: '',
              seatRemain: flight.remainSeats,
              fareDataId: flight.fareDataId,
              newFareDataId: 0,
              totalPrice: totalNetPrice,
              seg: segments.length,
              flightSegmentGroupId: 0,
              listSegment: segments,
            },
          ];

          // Build fare data for each flight
          const mappedFareData: FareData = {
            trDetailId: 0,
            paymentType: PaymentMethod.None,
            bookingCode: '',
            bookingImage: '',
            expiryDate: expiredDate.toString(),
            groupClass: flight.groupClass,
            status: BookingStatus.Pending,
            message: flight.message || '',
            responseTime: new Date().toISOString(),
            duration: listFlight[0].duration,
            remark: '',
            issueTicketJson: '',

            // FareDataDataCom fields
            listFlight: listFlight,
            seesionId: flight.session,

            // FareDataModel fields
            bookingKey: flight.token,
            flightSegmentGroupId: 0,
            fareDataId: flight.fareDataId,
            newFareDataId: 0,
            airline: segments[0]?.airline || '',
            airlineName: segments[0]?.airlineName || '',
            itinerary: 0,
            leg: flight.leg,
            promo: false,
            currency: flight.currency || 'VND',
            system: '',
            adt: data.adult,
            chd: data.child,
            inf: data.infant,
            fareAdt:
              flight.prices.find((p: any) => p.type === PassengerType.Adult)
                ?.price || 0,
            fareChd:
              flight.prices.find((p: any) => p.type === PassengerType.Child)
                ?.price || 0,
            fareInf:
              flight.prices.find((p: any) => p.type === PassengerType.Infant)
                ?.price || 0,
            taxAdt:
              flight.prices.find((p: any) => p.type === PassengerType.Adult)
                ?.tax || 0,
            taxChd:
              flight.prices.find((p: any) => p.type === PassengerType.Child)
                ?.tax || 0,
            taxInf:
              flight.prices.find((p: any) => p.type === PassengerType.Infant)
                ?.tax || 0,
            feeAdt:
              flight.prices.find((p: any) => p.type === PassengerType.Adult)
                ?.fee || 0,
            feeChd:
              flight.prices.find((p: any) => p.type === PassengerType.Child)
                ?.fee || 0,
            feeInf:
              flight.prices.find((p: any) => p.type === PassengerType.Infant)
                ?.fee || 0,
            serviceFeeAdt: 0,
            originalAmount: totalNetPrice,
            originalAdtAmount:
              (flight.prices.find((p: any) => p.type === PassengerType.Adult)
                ?.price ?? 0) +
              (flight.prices.find((p: any) => p.type === PassengerType.Adult)
                ?.tax || 0) +
              (flight.prices.find((p: any) => p.type === PassengerType.Adult)
                ?.fee || 0),
            originalChdAmount:
              (flight.prices.find((p: any) => p.type === PassengerType.Child)
                ?.price || 0) +
              (flight.prices.find((p: any) => p.type === PassengerType.Child)
                ?.tax || 0) +
              (flight.prices.find((p: any) => p.type === PassengerType.Child)
                ?.fee || 0),
            originalInfAmount:
              (flight.prices.find((p: any) => p.type === PassengerType.Infant)
                ?.price || 0) +
              (flight.prices.find((p: any) => p.type === PassengerType.Infant)
                ?.tax || 0) +
              (flight.prices.find((p: any) => p.type === PassengerType.Infant)
                ?.fee || 0),
            serviceFeeChd: 0,
            serviceFeeInf: 0,
            serviceFeeInfOriginal: 0,
            serviceFeeChdOriginal: 0,
            serviceFeeAdtOriginal: 0,
            totalNetPrice: totalNetPrice,
            totalServiceFee: 0,
            totalPrice: totalPrice,
            fullPrice: totalPrice,
            fullPriceOriginal: totalPrice,
            expiredDate: expiredDate.toString(),
          };

          return mappedFareData;
        });

        // Map passengers and add empty baggage list
        const passengers: Passenger[] = data.listPassenger.map(
          (p: MyBBookingPassenger, index: number) => {
            const listBaggage: Baggage[] = p.ancillaries.map(
              (a: MyBBookingAncillary) => {
                return {
                  airline: a.airline,
                  leg: a.leg,
                  route: a.route,
                  code: a.code,
                  currency: a.currency,
                  name: a.name,
                  price: a.price,
                  value: a.value,
                  trCusId: p.id,
                };
              },
            );

            return {
              id: p.id,
              index: index,
              firstName: p.firstName,
              lastName: p.lastName || '',
              type: p.type,
              gender: p.gender,
              birthday: p.birthday || '',
              key: `passenger_${index}`,
              identifier: p.identifier || '',
              country: p.country || '',
              issuingCountry: p.issuingCountry || '',
              expiryDate: p.expiryDate || '',
              issuingDate: p.issuingDate || '',
              listBaggage,
            };
          },
        );

        // The THANH TOÁN preview total must equal the sum of the per-leg fares
        // rendered in section 3 (listFareData[i].totalPrice), which is also what
        // the server review-booking total resolves to. The previous
        // calculateTotalAmt() fell back to bookingChecked.flights, which for a
        // round-trip can total only the outbound leg and flash a one-way price
        // during the load window until the review-booking response arrives.
        const previewTotalAmt = fareData.reduce(
          (sum, fare) => sum + fare.totalPrice,
          0,
        );

        // Create GetMyBookingDetailResponse object
        const response: GetMyBookingDetailResponse = {
          status: 200,
          message: '',
          id: state.bookingCreated?.bookingId ?? 0,
          trCode: '',
          oneway: data.flights.length === 1,
          note: data.note,
          accCode: data.accCode,
          agCode: data.agCode,
          vat: data.vat,
          newTotalFare: newTotalFare,
          totalAmt: previewTotalAmt,
          paymentFee: 0,
          createDate: createDate.toString(),
          paymentType: PaymentMethod.None,
          billingCode: '',
          coinClaim: false,
          local: data.local,
          isAfter24H: false,
          allowSendSms: false,
          isCombo: data.isCombo,
          coupon: {
            codeId: 0,
            code: '',
            discount: 0,
            cash: false,
            afterDiscount: 0,
            showTicket: false,
            message: '',
          },
          contact: {
            gender: data.contact.gender,
            firstName: data.contact.firstName,
            lastName: data.contact.lastName || '',
            phone: data.contact.phone,
            email: data.contact.email,
            address: data.contact.address || '',
            note: data.contact.note || '',
            createDate: data.contact.createDate,
            ipAddress: data.contact.ipAddress,
            agentPhone: data.contact.agentPhone || '',
            agentEmail: data.contact.agentEmail || '',
            agentName: data.contact.agentName || '',
          },
          listPassenger: passengers,
          listFareData: fareData,
          bookingLogs: [],
        };

        return response;
      };

      patchState(store, (state) => {
        const bookingRequest = state.bookingRequest;
        if (bookingRequest) {
          const preloadReviewBooking = mapBookingRequestToReviewBooking(state);
          return {
            paymentSuccess: {
              ...state.paymentSuccess,
              preloadReviewBooking: { ...preloadReviewBooking },
            },
          };
        }
        return state;
      });
    },

    updatePrefetchReviewBooking(data: PrefetchBookingNotifyModel) {
      patchState(store, (state) => {
        const preloadReviewBooking = state.paymentSuccess.preloadReviewBooking;
        if (
          preloadReviewBooking &&
          preloadReviewBooking.id === data.bookingId
        ) {
          for (const fareData of preloadReviewBooking.listFareData) {
            const bookingPrefetch = data.data.find(
              (b) => b.Leg === fareData.leg,
            );
            if (bookingPrefetch) {
              fareData.trDetailId = bookingPrefetch.DetailId;
              fareData.bookingCode = bookingPrefetch.BookingCode;
            }
          }

          return {
            paymentSuccess: {
              ...state.paymentSuccess,
              preloadReviewBooking: { ...preloadReviewBooking },
            },
          };
        }

        return state;
      });
    },

    updateBookingCreated(bookingCreated: MyBCreateBookingResponse) {
      patchState(store, () => ({
        bookingCreated: { ...bookingCreated },
      }));
    },

    updateBookingChecked(bookingChecked: MybCheckBookingResponse) {
      patchState(store, () => ({
        bookingChecked: { ...bookingChecked },
      }));
    },

    updatePaymentState(paymentState: Partial<PaymentState>) {
      patchState(store, (state) => ({
        payment: {
          ...state.payment,
          ...paymentState,
        },
      }));
    },

    resetPaymentState() {
      patchState(store, () => ({
        payment: { ...initialState.payment },
      }));
    },

    updatePaymentSuccessState(paymentSuccess: Partial<PaymentSuccessState>) {
      patchState(store, (state) => ({
        paymentSuccess: {
          ...state.paymentSuccess,
          ...paymentSuccess,
        },
      }));
    },

    resetPaymentSuccessState() {
      patchState(store, () => ({
        paymentSuccess: { ...initialState.paymentSuccess },
      }));
    },
  })),
  withHooks((store) => ({
    onInit() {
      if (environment.debug) {
        effect(() => {
          console.log('[FILTER_STATE]', store.filter());
        });
        effect(() => {
          console.log('[SERVICES_BY_VENDOR]', store.servicesByVendor());
        });
        effect(() => {
          console.log('[PRICE_TERMS]', store.priceTerms());
        });
        effect(() => {
          console.log(
            '[SELECT_FLIGHT_CONDITIONS_STATE]',
            store.selectFlightConditions(),
          );
        });
        effect(() => {
          console.log('[SELECT_FLIGHT_STATE]', store.selectFlight());
        });
        effect(() => {
          console.log('[SELECTED_FLIGHT]', store.selectFlight.selectedFlight());
          console.log(
            '[SELECTED_ROUNDTRIP_FLIGHT]',
            store.selectFlight.selectedRoundTripFlight(),
          );
          console.log(
            '[SELECTED_COMBO]',
            store.selectFlight.selectedSmartCombo(),
          );
        });
        effect(() => {
          console.log('[RESELECT_FLIGHT_STATE]', store.reselectFlight());
        });
        effect(() => {
          console.log(
            '[SESSION]',
            store.sessionExpired()
              ? dayjs(store.sessionExpired()).format('DD/MM/YYYY HH:mm:ss')
              : null,
          );
          console.log('[SESSION_TIME_OUT]', store.sessionTimeOutRef());
        });
        effect(() => {
          console.log('[PASSENGER_INFO_STATE]', store.passengerInfo());
        });
        effect(() => {
          console.log('[BOOKING_REQUEST_STATE]', store.bookingRequest());
        });
        effect(() => {
          console.log('[BOOKING_CHECKED_STATE]', store.bookingChecked());
        });
        effect(() => {
          console.log('[BOOKING_CREATED_STATE]', store.bookingCreated());
        });
        effect(() => {
          console.log('[SELECT_SERVICE_STATE]', store.selectService());
        });
        effect(() => {
          console.log('[PAYMENT_STATE]', store.payment());
        });
        effect(() => {
          console.log('[PAYMENT_SUCCESS_STATE]', store.paymentSuccess());
        });
      }
    },
  })),
);
